import { Canvas } from "fabric";
import { useState, useCallback, useEffect } from "react";
import { useSaveOperations } from "./use-save-operations";
import { useRateLimit } from "./use-rate-limit";
import { useAuth } from "@/components/AuthProvider";
import { useSessionHandler } from "./use-session-handler";
import { toast } from "@/hooks/use-toast";
import { useVersionControl } from "./use-version-control";
import { sanitizeTags } from "@/utils/sanitization";
import { ConflictDialogProps } from "@/components/toolbar/save-drawing/ConflictDialog";

interface UseDrawingSaveProps {
  canvas: Canvas | null;
  currentDrawingId: string | null;
  title: string;
  onDrawingSaved: (id: string) => void;
  onRefreshNeeded?: (refreshFn: () => void) => void;
}

// Maximum number of retry attempts
const MAX_RETRIES = 3;
// Initial delay between retries in milliseconds
const INITIAL_RETRY_DELAY = 1000;

export const useDrawingSave = ({
  canvas,
  currentDrawingId,
  title,
  onDrawingSaved,
  onRefreshNeeded,
}: UseDrawingSaveProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflictType, setConflictType] = useState<'minor' | 'major'>('minor');
  const [serverVersion, setServerVersion] = useState<number | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [retryDelay, setRetryDelay] = useState(INITIAL_RETRY_DELAY);
  const [pendingOperation, setPendingOperation] = useState<{
    type: 'save' | 'saveAs';
    description: string;
    tags: string[];
  } | null>(null);
  
  const { user } = useAuth();
  const { handleSessionError, isSessionExpired } = useSessionHandler();
  const { saveExistingDrawing, saveNewDrawing, quickSave } = useSaveOperations({
    canvas,
    title,
    currentDrawingId,
    onDrawingSaved,
    onRefreshNeeded,
  });
  const { checkRateLimit } = useRateLimit();
  const { getCanvasVersion } = useVersionControl();

  // Reset error states
  const resetErrorStates = useCallback(() => {
    setHasError(false);
    setHasTimedOut(false);
    setRetryCount(0);
    setRetryDelay(INITIAL_RETRY_DELAY);
    
    // Clear any existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      setSaveTimeout(null);
    }
  }, [saveTimeout]);

  // Set up timeout handling
  const setupSaveTimeout = useCallback(() => {
    // Clear any existing timeout
    resetErrorStates();
    
    // Set a new timeout that will trigger after 10 seconds
    const timeout = setTimeout(() => {
      if (isSaving) {
        setHasTimedOut(true);
        console.warn("Save operation timed out");
      }
    }, 10000);
    
    setSaveTimeout(timeout);
    
    return () => {
      clearTimeout(timeout);
    };
  }, [isSaving, resetErrorStates]);

  // Effect to handle auto-retry on timeout
  useEffect(() => {
    if (hasTimedOut && retryCount < MAX_RETRIES && pendingOperation) {
      console.log(`Auto-retrying save operation (${retryCount + 1}/${MAX_RETRIES}) in ${retryDelay}ms`);
      
      const retryTimer = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setHasTimedOut(false);
        
        if (pendingOperation.type === 'save') {
          handleQuickSave(pendingOperation.description, pendingOperation.tags, true);
        } else {
          handleSaveAs(pendingOperation.description, pendingOperation.tags, true);
        }
      }, retryDelay);
      
      // Increase delay for next retry (exponential backoff)
      setRetryDelay(prev => Math.min(prev * 1.5, 10000));
      
      return () => clearTimeout(retryTimer);
    }
  }, [hasTimedOut, retryCount, retryDelay, pendingOperation]);

  // Handle conflict resolution
  const handleConflictResolution = useCallback(async (resolution: 'reload' | 'overwrite' | 'copy') => {
    if (!pendingOperation) return;
    
    setShowConflictDialog(false);
    
    if (resolution === 'reload') {
      // Reload the latest version
      toast({
        title: "Loading latest version",
        description: "Reloading the latest version of this drawing.",
      });
      
      // The onRefreshNeeded callback should handle loading the latest version
      if (onRefreshNeeded) {
        onRefreshNeeded(() => {});
      }
    } else if (resolution === 'overwrite') {
      // Force overwrite using a different approach (to be implemented)
      toast({
        title: "Force overwrite",
        description: "Attempting to overwrite with your changes.",
      });
      
      // This would require implementing a different API call that bypasses version checks
      // For now, this is a placeholder
      console.log("Force overwrite not implemented yet");
    } else if (resolution === 'copy') {
      // Save as a new copy
      handleSaveAs(pendingOperation.description, pendingOperation.tags);
    }
  }, [pendingOperation, onRefreshNeeded]);

  const handleQuickSave = async (description: string = "", tags: string[] = [], isRetry = false) => {
    console.log("QuickSave called with:", { description, tags, isRetry });
    
    // Store operation details for potential retry
    if (!isRetry) {
      setPendingOperation({
        type: 'save',
        description,
        tags
      });
    }
    
    // Check rate limiting
    if (!checkRateLimit()) {
      console.log("Rate limit check failed");
      return false;
    }
    
    // Validate session state
    if (isSessionExpired) {
      console.log("Session expired, attempting refresh");
      const sessionValid = await handleSessionError();
      if (!sessionValid) {
        console.log("Session validation failed");
        return false;
      }
    }
    
    setIsSaving(true);
    setHasError(false);
    setHasTimedOut(false);
    
    // Set up timeout handling
    setupSaveTimeout();
    
    try {
      // Sanitize tags before saving
      const sanitizedTags = sanitizeTags(tags);
      
      // Call the consolidated save operation
      const result = await quickSave(description, sanitizedTags);
      
      if (result && typeof result === 'object' && 'success' in result) {
        // Check for version conflict and handle as a specific case
        if (!result.success && 'conflictType' in result) {
          setConflictType(result.conflictType as 'minor' | 'major');
          if ('serverVersion' in result && typeof result.serverVersion === 'number') {
            setServerVersion(result.serverVersion);
          } else {
            setServerVersion(null);
          }
          setShowConflictDialog(true);
          return false;
        }
      }
      
      resetErrorStates();
      return result;
    } catch (error) {
      console.error("Error in handleQuickSave:", error);
      setHasError(true);
      toast({
        variant: "destructive",
        title: "Save failed",
        description: error instanceof Error ? error.message : "There was an error saving your drawing. Please try again.",
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAs = async (description: string, tags: string[], isRetry = false) => {
    console.log("SaveAs called with:", { description, tags, isRetry });
    
    // Store operation details for potential retry
    if (!isRetry) {
      setPendingOperation({
        type: 'saveAs',
        description,
        tags
      });
    }
    
    // Check rate limiting
    if (!checkRateLimit()) {
      console.log("Rate limit check failed");
      return false;
    }

    // Validate session state
    if (isSessionExpired) {
      console.log("Session expired, attempting refresh");
      const sessionValid = await handleSessionError();
      if (!sessionValid) {
        console.log("Session validation failed");
        return false;
      }
    }
    
    setIsSaving(true);
    setHasError(false);
    setHasTimedOut(false);
    
    // Set up timeout handling
    setupSaveTimeout();
    
    try {
      // Sanitize tags before saving
      const sanitizedTags = sanitizeTags(tags);
      
      // Attempt save with error handling
      const success = await saveNewDrawing(description, sanitizedTags);
      resetErrorStates();
      return success;
    } catch (error) {
      console.error("Error in handleSaveAs:", error);
      setHasError(true);
      toast({
        variant: "destructive",
        title: "Save failed",
        description: error instanceof Error ? error.message : "There was an error saving your drawing. Please try again.",
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleRetry = async () => {
    console.log("Retrying save operation");
    setHasError(false);
    setHasTimedOut(false);
    
    if (!pendingOperation) {
      console.warn("No pending operation to retry");
      return false;
    }
    
    if (pendingOperation.type === 'save') {
      return handleQuickSave(pendingOperation.description, pendingOperation.tags, true);
    } else {
      return handleSaveAs(pendingOperation.description, pendingOperation.tags, true);
    }
  };

  return {
    handleQuickSave,
    handleSaveAs,
    handleRetry,
    isSaving,
    hasTimedOut,
    hasError,
    showConflictDialog,
    setShowConflictDialog,
    handleConflictResolution,
    conflictType,
    serverVersion
  };
};
